import React, { useEffect, useState } from 'react';
import { BasePage } from '@/pages';
import { AuthProvider, AuthenticatedRoute, getPortalUrl } from './auth';
import LoadingPage from './components/Loading';

const parseCookies = (): Record<string, string> =>
  document.cookie
    .split('; ')
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const separatorIndex = pair.indexOf('=');
      const key = decodeURIComponent(pair.slice(0, separatorIndex));
      acc[key] = decodeURIComponent(pair.slice(separatorIndex + 1));
      return acc;
    }, {});

/**
 * The Portal writes Amplify's CognitoIdentityServiceProvider.* token keys as cookies on the
 * shared domain; copying them into localStorage lets Amplify resume that session here.
 */
const initSessionFromPortal = () => {
  localStorage.clear();
  Object.entries(parseCookies()).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
  localStorage.setItem('referrer', 'portal');
};

const isSameOrigin = (url: string | undefined, target: string | undefined) => {
  if (!url || !target) return false;
  try {
    return new URL(url).origin === new URL(target).origin;
  } catch {
    return false;
  }
};

const isLocalhostEnv = () => window.location.hostname.includes('localhost');

// Covers page refresh and direct navigation after a Portal hand-off.
const hasStoredSession = () =>
  Boolean(localStorage.getItem('userIdToken') || localStorage.getItem('referrer'));

const App: React.FC = () => {
  const [isSessionDataLoaded, setIsSessionDataLoaded] = useState(false);

  useEffect(() => {
    if (isSameOrigin(document.referrer, getPortalUrl())) {
      initSessionFromPortal();
      setIsSessionDataLoaded(true);
    } else if (hasStoredSession() || isLocalhostEnv()) {
      // AuthProvider/AuthenticatedRoute validate the stored session.
      setIsSessionDataLoaded(true);
    } else {
      window.location.href = getPortalUrl();
    }
  }, []);

  if (!isSessionDataLoaded) {
    return <LoadingPage />;
  }

  return (
    <AuthProvider>
      <AuthenticatedRoute>
        <BasePage />
      </AuthenticatedRoute>
    </AuthProvider>
  );
};

export default App;
